import { useEffect, useMemo, useState } from "react";
import {
	useMaterialReactTable,
	MaterialReactTable,
} from "material-react-table";
import { Controller, useForm } from "react-hook-form";

import "./Admin.scss";

import { Input, Label } from "abs-ui-components";

import { emitToasterMsg } from "../../../services/toasterUtility";
import { getRolesInfo, addNewRole } from "../../../services/AdminService";
import AbsModal from "../../../common/absModal/AbsModal";
import {
	DESCRIPTION_FOR_ROLES,
	ROLE,
	ROLE_DESCRIPTION,
} from "./Admin_constants";

const RoleDetails = () => {
	const [rolesInfoData, setRolesInfoData] = useState([]);
	const [filterSearchValue, SetfilterSearchValue] = useState("");
	const [totalRolesList, setTotalRolesList] = useState([]);
	const [totalRolesCount, setTotalRolesCount] = useState(0);
	const [addRole, setAddRole] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const {
		control,
		formState: { errors },
		handleSubmit,
	} = useForm();

	useEffect(() => {
		getRolesDetails();
	}, []);

	useEffect(() => {
		roleFilterData();
	}, [filterSearchValue]);

	const getRolesDetails = async () => {
		setLoading(true);
		try {
			const response = await getRolesInfo();
			if (response && response?.isSuccess) {
				setRolesInfoData(response?.data?.roles);
				setTotalRolesCount(response?.data?.roleCount);
				setTotalRolesList(response?.data?.roles);
				setLoading(false);
			}
		} catch (error) {
			emitToasterMsg("error", error.response);
		}
	};

	const rolesColumns = useMemo(
		() => [
			{
				header: "Role Name",
				enableHiding: false,
				accessorKey: "normalizedName",
			},
			{
				header: "Description",
				accessorKey: "id",
				accessorFn: (row) => {
					if (row?.normalizedName)
						return DESCRIPTION_FOR_ROLES[row?.normalizedName];
				},
			},
		],
		[]
	);

	const toggleOpen = () => {
		setAddRole(!addRole);
	};

	const roleFilterData = () => {
		const filteredRoles = rolesInfoData.filter((roles) => {
			let searchRoleValueToLowerCase = filterSearchValue?.toLowerCase();
			return (
				roles.normalizedName
					?.toLowerCase()
					.includes(searchRoleValueToLowerCase) ||
				roles.id?.toLowerCase().includes(searchRoleValueToLowerCase)
			);
		});
		setRolesInfoData(filteredRoles);
		if (filterSearchValue === "") {
			setRolesInfoData(totalRolesList);
		}
	};

	const handleRoleName = async (formdata) => {
		const userRole = formdata?.roleName;
		try {
			const response = await addNewRole(userRole);
			if (response && response.isSuccess) {
				emitToasterMsg("success", "Created Successfully");
				setAddRole(false);
			} else {
				emitToasterMsg("error", response.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", error.response);
		}
	};

	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const userDetailsTable = useMaterialReactTable({
		columns: rolesColumns,
		data: rolesInfoData,
		enableRowSelection: false,
		manualSorting: false,
		enableStickyHeader: true,
		manualPagination: true,
		enableToolbarInternalActions: false,
		enableColumnActions: false,
		enablePagination: false,
		enableRowActions: false,
		positionActionsColumn: "last",
		initialState: {
			density: "compact",
			columnOrder: ["Role Name", "Id"],
		},
		state: {
			isLoading: isLoading,
		},

		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 376px)",
			},
		},

		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
				"& .MuiSvgIcon-root": {
					color: "white",
				},
			},
		},
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
	});

	return (
		<>
			<div className="d-flex justify-content-between">
				<h4>Roles ({totalRolesCount})</h4>
				{/* Future scope <Button
					onClick={toggleOpen}
					startIcon={<Icon icon={ICONS.ADD} />}
				>
					Add Role
				</Button> */}
			</div>
			<div className="mt-2">
				<MaterialReactTable table={userDetailsTable} />
			</div>
			<AbsModal
				isOpen={addRole}
				title="Add Role"
				onClose={setAddRole}
				close={toggleOpen}
			>
				<div className="pb-5">
					<div>
						{Object.values(ROLE).map((title, index) => (
							<h6
								className="add-role-titles p-3"
								key={`newrole-${index}`}
							>
								{title}
							</h6>
						))}
					</div>
					<ul className="membership-container mt-4">
						{Object.values(ROLE_DESCRIPTION).map((info, index) => (
							<li className="pt-2" key={`description-${index}`}>
								{info}
							</li>
						))}
					</ul>
					<form onSubmit={handleSubmit(handleRoleName)}>
						<div className="form-container p-3">
							<Label name="Role Name" required bold />
							<Controller
								name="roleName"
								control={control}
								rules={{
									required: "Role is required",
								}}
								render={({ field }) => (
									<>
										<Input
											id="roleName"
											type="text"
											onChange={field.onChange}
											value={field.value}
											autoFocus={false}
											className="name-input mt-2"
											placeholder="Enter role name"
										/>
										{errors["roleName"] && (
											<p className="error">
												{errors["roleName"].message}
											</p>
										)}
									</>
								)}
							/>
							<button
								type="submit"
								className="change-password text-center mt-3 "
							>
								{" "}
								Create
							</button>
						</div>
					</form>{" "}
				</div>
			</AbsModal>
		</>
	);
};

export default RoleDetails;
